import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Home, Search, Users, Bell, Settings, Shield, Palette, UserCircle, ListChecks, Sparkles, Plus } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { currentReviewSession, loadSharedWebReviews } from '../../lib/reviewFeedCache';
import { useTheme } from '../../lib/theme';
import { iosHeroCss } from '../../lib/iosAppearance';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { authAPI, notificationsAPI } from '../../lib/api';
import API from '../../lib/api';
import PikBrand from '../brand/PikBrand';
import { toast } from 'sonner';
import { ContentScaleProvider, ShellBody } from './ContentScale';
import AddMenuSheet from './AddMenuSheet';

const footerItems = [
  { to: '/', icon: Home, label: 'Home' },
  { to: '/search', icon: Search, label: 'Search All' },
  { to: '/groups', icon: Users, label: 'Groups' },
  { action: 'add', icon: Plus, label: 'Add' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];
const sidebarItems = [
  ...footerItems.slice(0, 4),
  { to: '/manage-contacts', icon: ListChecks, label: 'Manage contacts' },
  footerItems[4],
];

function mediaUrl(raw) {
  const value = String(raw || '').trim();
  if (!value) return '';
  if (value.startsWith('/api')) {
    return `${process.env.REACT_APP_BACKEND_URL || ''}${value}`;
  }
  return value;
}

function ProfileAvatar({ photo, initials, light, size = 36 }) {
  const ring = light ? 'rgba(58,37,48,0.35)' : 'rgba(255,255,255,0.7)';
  const fallbackBg = light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.22)';
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border-2"
      style={{ width: size, height: size, borderColor: ring, background: fallbackBg }}
      data-testid="header-profile-avatar"
    >
      {photo ? (
        <img src={photo} alt="" className="h-full w-full object-cover" data-testid="header-profile-photo" />
      ) : initials ? (
        <span className="text-xs font-bold" data-testid="header-profile-initials">{initials}</span>
      ) : (
        <UserCircle size={Math.round(size * 0.55)} data-testid="header-profile-icon" />
      )}
    </span>
  );
}

export default function AppShell() {
  const { user } = useAuth();
  const { currentTheme, theme, themes } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [reviewCount, setReviewCount] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [myContactId, setMyContactId] = useState(() => localStorage.getItem('pik_my_contact_id'));
  const [contactPhoto, setContactPhoto] = useState('');

  const lastRefreshRef = useRef(0);
  const refreshCounts = useCallback(() => {
    const startedUser = user?.id || '';
    if (!startedUser) return;
    const now = Date.now();
    if (now - lastRefreshRef.current < 2000) return; // Throttle to max once per 2s
    lastRefreshRef.current = now;
    // Bell = items to approve ONLY: PeopleiKnow "Needs review" + Apple inbound
    // decisions (GET /notifications/reviews, de-duped + healed). Web has no
    // device address book, so there's no outbound "send to phone" bucket here.
    // Informational/unread notifications are deliberately NOT counted.
    const startedSession = currentReviewSession();
    loadSharedWebReviews(startedUser, () => notificationsAPI.reviews().then(({ data }) => data))
      .then((feed) => {
        if (currentReviewSession() !== startedSession) return;
        if (feed && typeof feed.serverCount === 'number') setReviewCount(feed.serverCount);
      }).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    refreshCounts();
    authAPI.getMyContact().then(({ data }) => {
      if (data?.id) {
        setMyContactId(data.id);
        localStorage.setItem('pik_my_contact_id', data.id);
      }
      setContactPhoto(mediaUrl(data?.image_url || data?.photo_url));
    }).catch(() => {});
    notificationsAPI.generate().catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for custom events to dynamically refresh counts
  useEffect(() => {
    const handler = () => refreshCounts();
    window.addEventListener('pik-refresh-counts', handler);
    return () => window.removeEventListener('pik-refresh-counts', handler);
  }, [refreshCounts]);

  // Re-sync myContactId and refresh counts on navigation
  useEffect(() => {
    const stored = localStorage.getItem('pik_my_contact_id');
    if (stored && stored !== myContactId) {
      setMyContactId(stored);
    }
    API.post('/admin/track/pageview', { page: location.pathname }).catch(() => {});
    refreshCounts();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAppearance = () => navigate('/appearance');
  const onHome = location.pathname === '/';

  const goToMyContact = async () => {
    // Resolve on click through the self-healing endpoint so a stale cached id
    // can never 404 ("Contact not found") on the avatar path.
    try {
      const { data } = await authAPI.getMyContact();
      if (data?.id) {
        setMyContactId(data.id);
        localStorage.setItem('pik_my_contact_id', data.id);
        navigate(`/contacts/${data.id}`);
        return;
      }
    } catch (e) { /* fall back below */ }
    if (myContactId) {
      navigate(`/contacts/${myContactId}`);
      return;
    }
    navigate('/settings');
    toast.info('Set your contact card by clicking "My Contact Card" below');
  };

  const badgeCount = reviewCount || 0;
  const userInitials = `${(user?.first_name || '')[0] || ''}${(user?.last_name || '')[0] || ''}`.toUpperCase();
  const userName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'My account';
  const profilePhoto = mediaUrl(user?.photo_url || user?.image_url) || contactPhoto;
  // Classic Blue's headerGradient is the iOS/Home hero band. The fallback
  // is that same band, not the previous #0F52BA → #3B82F6 shade.
  const headerBackground = currentTheme?.headerGradient || iosHeroCss('blue');

  // Blush uses a PALE header gradient (matches the mobile hero + the approved
  // mockup), so the header/sidebar text must flip to dark for legibility.
  const headerLight = !!currentTheme?.headerIsLight;
  const hdrStrong = headerLight ? 'text-foreground' : 'text-white';
  const hdrSoft = headerLight ? 'text-foreground/70 hover:text-foreground' : 'text-white/80 hover:text-white';
  const hdrHoverBg = headerLight ? 'hover:bg-foreground/5' : 'hover:bg-white/10';
  const navActiveCls = headerLight ? 'bg-foreground/10 font-semibold text-foreground' : 'bg-white/20 font-semibold text-white';
  const navIdleCls = headerLight ? 'text-foreground/70 hover:bg-foreground/5 hover:text-foreground' : 'text-white/75 hover:bg-white/10 hover:text-white';
  // Wordmark on the pale Blush header uses a deep-rose brand colour (never black,
  // per user request); other themes keep the inherited white/dark text.
  const hdrLogoColor = '#B0466B';

  return (
    <ContentScaleProvider>
    <div className="app-container bg-background lg:flex-row" data-testid="app-shell">
      {/* Desktop sidebar (≥1024px) — replaces the bottom nav + gradient header,
          uses the horizontal space so laptops aren't a stretched phone. */}
      <aside
        className={`hidden lg:flex lg:flex-col w-64 shrink-0 h-full ${hdrStrong} px-3 py-5`}
        style={{ background: headerBackground }}
        data-testid="app-sidebar"
      >
        <button
          onClick={() => navigate('/')}
          className="px-3 mb-8 text-left"
          data-testid="sidebar-logo"
        >
          <PikBrand onDark={!headerLight} color={headerLight ? hdrLogoColor : '#FFFFFF'} size="1.6rem" testId="sidebar-brand" />
        </button>

        <nav className="flex flex-col gap-1">
          {sidebarItems.map(({ to, icon: Icon, label, action }) => (
            action === 'add' ? (
              <button
                key={label}
                type="button"
                onClick={() => setAddOpen(true)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${navIdleCls}`}
                data-testid="sidebar-nav-add"
              >
                <Plus size={19} strokeWidth={2.25} />
                <span>Add</span>
              </button>
            ) : (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => {
                const on = isActive || (to === '/intros' && location.pathname === '/share');
                return `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  on ? navActiveCls : navIdleCls
                }`;
              }}
              data-testid={`sidebar-nav-${label.toLowerCase().replace(/&/g, 'and').replace(/\s+/g, '-')}`}
            >
              <Icon size={19} />
              <span>{label}</span>
            </NavLink>
            )
          ))}
        </nav>

        <div className="flex-1" />

        {/* Quick actions */}
        <div className="flex items-center gap-1 px-1 mb-3">
          <button
            onClick={() => navigate('/updates')}
            className={`relative p-2 rounded-lg ${hdrSoft} ${hdrHoverBg} transition-colors`}
            title="Notifications"
            data-testid="sidebar-notifications-btn"
          >
            <Bell size={18} />
            {badgeCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center">
                {badgeCount > 9 ? '9+' : badgeCount}
              </span>
            )}
          </button>
          <button
            onClick={openAppearance}
            className={`p-2 rounded-lg ${hdrSoft} ${hdrHoverBg} transition-colors`}
            title="Appearance and text size"
            aria-label="Appearance and text size"
            data-testid="sidebar-theme-toggle"
          >
            <Palette size={18} />
          </button>
          {user?.is_admin && (
            <button
              onClick={() => navigate('/admin')}
              className={`p-2 rounded-lg ${hdrSoft} ${hdrHoverBg} transition-colors`}
              title="Admin"
              data-testid="sidebar-admin-btn"
            >
              <Shield size={18} />
            </button>
          )}
        </div>

        <button
          onClick={goToMyContact}
          className={`flex items-center gap-3 px-2 py-2 rounded-xl ${hdrHoverBg} transition-colors text-left`}
          data-testid="sidebar-my-contact-btn"
        >
          <ProfileAvatar photo={profilePhoto} initials={userInitials} light={headerLight} />
          <span className="text-sm font-medium truncate">{userName}</span>
        </button>
      </aside>

      {/* Content column */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        {/* Mobile / tablet gradient header (hidden on desktop — sidebar owns brand) */}
        {!onHome && <header
          className="lg:hidden shrink-0 z-50 px-4 py-3 flex items-center justify-between rounded-b-xl relative overflow-hidden"
          style={{ background: headerBackground }}
          data-testid="app-header"
        >
          {/* Header shimmer: synced 24s sheen + twinkling star sweep the top bar
              (Blush + Midnight Indigo only). CSS-gated; non-interactive. */}
          <div className="pik-header-ambience" aria-hidden="true" data-testid="header-ambience">
            <div className="pik-header-ambience__sheen" />
            <span className="pik-header-spark" style={{ top: '22%', left: '26%' }}>
              <Sparkles size={13} strokeWidth={2} />
            </span>
            <span className="pik-header-spark" style={{ top: '55%', left: '68%', animationDelay: '2.6s' }}>
              <Sparkles size={11} strokeWidth={2} />
            </span>
          </div>
          <button
            onClick={goToMyContact}
            className={`relative z-10 ${hdrSoft} transition-colors rounded-full p-0.5 -ml-1`}
            title={myContactId ? 'My Contact' : 'Set up your contact card'}
            data-testid="header-my-contact-btn"
          >
            <ProfileAvatar photo={profilePhoto} initials={userInitials} light={headerLight} />
          </button>
          <h1
            className="relative z-10 tracking-tight cursor-pointer"
            onClick={() => navigate('/')}
            data-testid="app-logo"
          >
            <PikBrand onDark={!headerLight} color={headerLight ? hdrLogoColor : '#FFFFFF'} size="1.35rem" testId="app-logo-brand" />
          </h1>
          <div className="relative z-10 flex items-center gap-1">
            <button
              onClick={() => navigate('/updates')}
              className={`${hdrSoft} transition-colors p-2.5 relative`}
              title="Notifications"
              data-testid="header-notifications-btn"
            >
              <Bell size={18} />
              {badgeCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center" data-testid="notification-badge">
                  {badgeCount > 9 ? '9+' : badgeCount}
                </span>
              )}
            </button>
            <button
              onClick={openAppearance}
              className={`${hdrSoft} transition-colors p-2.5 relative`}
              title="Appearance and text size"
              aria-label="Appearance and text size"
              data-testid="header-theme-toggle"
            >
              <Palette size={18} />
              <span
                className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white/30"
                style={{ backgroundColor: (themes?.[theme]?.navBlue) || '#0F52BA' }}
              />
            </button>
            {user?.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                className={`${hdrSoft} transition-colors p-2.5`}
                data-testid="header-admin-btn"
              >
                <Shield size={18} />
              </button>
            )}
          </div>
        </header>}

        {/* Main Content — the ONLY scrollable region. */}
        <main className="flex-1 overflow-y-auto overscroll-contain pb-4" data-testid="main-content">
          <ShellBody>
            <Outlet />
          </ShellBody>
        </main>

        {/* Bottom Nav — mobile / tablet only. */}
        <nav className={`glass-nav shrink-0 z-40 lg:hidden ${currentTheme?.isDark ? 'bg-card/90' : ''}`} data-testid="bottom-nav">
          <div className="max-w-[640px] mx-auto flex items-center justify-around h-16">
            {footerItems.map(({ to, icon: Icon, label, badge: hasBadge, action }) => (
              action === 'add' ? (
                <button
                  key={label}
                  type="button"
                  onClick={() => setAddOpen(true)}
                  className="flex flex-col items-center gap-0.5 px-3 py-1 relative text-muted-foreground"
                  data-testid="nav-add"
                >
                  <Plus size={20} strokeWidth={2.25} />
                  <span className="text-[10px] font-medium">Add</span>
                </button>
              ) : (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                onClick={(e) => {
                  if (to === '/search' && location.pathname === '/search') {
                    e.preventDefault();
                    const searchInput = document.querySelector('[data-testid="search-input"]');
                    if (searchInput) {
                      searchInput.focus();
                      searchInput.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }
                }}
                className={({ isActive }) => {
                  const on = isActive || (to === '/intros' && location.pathname === '/share');
                  return `flex flex-col items-center gap-0.5 px-3 py-1 relative transition-colors ${
                    on ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                  }`;
                }}
                data-testid={`nav-${label.toLowerCase().replace(/&/g, 'and').replace(/\s+/g, '-')}`}
              >
                {({ isActive }) => {
                  const on = isActive || (to === '/intros' && location.pathname === '/share');
                  return (
                  <>
                    <Icon size={20} strokeWidth={on ? 2.5 : 1.5} />
                    <span className="text-[10px] font-medium">{label}</span>
                    {on && (
                      <span className="absolute -bottom-0.5 w-1 h-1 rounded-full bg-accent" />
                    )}
                    {hasBadge && reviewCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center pulse-dot">
                        {reviewCount > 9 ? '9+' : reviewCount}
                      </span>
                    )}
                  </>
                );}}
              </NavLink>
              )
            ))}
          </div>
        </nav>
        <AddMenuSheet open={addOpen} onOpenChange={setAddOpen} onMyCard={goToMyContact} />
      </div>
    </div>
    </ContentScaleProvider>
  );
}
